import type { CoTErrorCode } from '@corner/shared';

// ─── Free-plan limits (mirrored from backend billingService) ─────
// Only free-plan limits are needed for proactive warnings since pro/enterprise are Infinity.
const FREE_PLAN_LIMITS = {
  aiConversations: 7,
  fileOperations: 20,
} as const;

/** Context available at error render time from existing app state. */
export interface CoTErrorRenderContext {
  userPlan?: 'free' | 'pro' | 'enterprise';
  usage?: { aiRequestsThisMonth: number; pagesThisMonth: number };
  failureCount: number;
  fileName?: string;
  toolName?: string;
}

/** CTA intent produced by the presentation layer. */
export interface CoTErrorCTA {
  label: string;
  /** 'upgrade' = navigate to billing; 'admin' = guidance for enterprise users; 'support' = persistent failure help */
  intent: 'upgrade' | 'admin' | 'support';
}

/** Presentation output for CoTErrorCard. */
export interface CoTErrorPresentation {
  title: string;
  body: string;
  /** @deprecated Use `cta` instead for actionable rendering. Kept for backwards compat. */
  showUpgrade: boolean;
  /**
   * Fallback retryability inferred from errorCode.
   * When backend provides retryable via SSE event, that takes precedence
   * (CoTErrorCard uses retryableOverride > presentation.retryable).
   */
  retryable: boolean;
  /** Optional CTA with intent — rendered as a real button/link by CoTErrorCard. */
  cta?: CoTErrorCTA;
  /** Optional quota warning shown before hard failure (e.g. "5 of 7 free requests used"). */
  quotaWarning?: string;
}

/** Friendly tool name for error messages. */
function friendlyTool(toolName?: string): string {
  if (!toolName) return 'this operation';
  const map: Record<string, string> = {
    pdf_to_word: 'PDF to Word conversion',
    word_to_pdf: 'document to PDF conversion',
    compress_pdf: 'PDF compression',
    merge_pdf: 'PDF merge',
    split_pdf: 'PDF split',
    ocr: 'text recognition',
    esign: 'document signing',
    sign_document: 'signature application',
    transcribe_audio: 'audio transcription',
    remove_background: 'background removal',
    generate_qr: 'QR code generation',
  };
  return map[toolName] ?? toolName.replace(/_/g, ' ');
}

// ─── Helpers ──────────────────────────────────────────────────────

function isEnterprise(ctx: CoTErrorRenderContext): boolean {
  return ctx.userPlan === 'enterprise';
}

function isFreePlan(ctx: CoTErrorRenderContext): boolean {
  return ctx.userPlan === 'free';
}

/** Build escalation text based on failure count tiers. */
function escalationText(failureCount: number, ctx: CoTErrorRenderContext): string {
  if (failureCount >= 5) {
    if (isEnterprise(ctx)) return ' If this persists, contact your organization admin for help.';
    return ' If this keeps happening, contact support.';
  }
  if (failureCount > 2) {
    return ' Try simplifying your request or using a different file.';
  }
  return '';
}

/** Compute proactive quota warning for free-plan users (non-error, informational). */
function computeQuotaWarning(ctx: CoTErrorRenderContext): string | undefined {
  if (!isFreePlan(ctx) || !ctx.usage) return undefined;

  const aiUsed = ctx.usage.aiRequestsThisMonth;
  const aiLimit = FREE_PLAN_LIMITS.aiConversations;
  const aiPct = aiUsed / aiLimit;

  if (aiPct >= 0.8 && aiUsed < aiLimit) {
    const remaining = aiLimit - aiUsed;
    return `${remaining} free AI request${remaining !== 1 ? 's' : ''} remaining this month.`;
  }
  return undefined;
}

/**
 * Extract signing-specific context from raw error messages.
 * Backend error messages for signing follow predictable patterns that include
 * field IDs and document IDs as quoted strings.
 */
function extractSigningContext(message: string): { fieldLabel?: string; documentName?: string } {
  const fieldMatch = message.match(/Field "([^"]+)"/i);
  const docMatch = message.match(/document "([^"]+)"/i);
  return {
    fieldLabel: fieldMatch?.[1],
    documentName: docMatch?.[1],
  };
}

/**
 * Maps a CoT error + app context to a user-facing presentation.
 * Falls back gracefully when metadata is absent.
 */
export function getContextualCoTErrorPresentation(
  error: { message: string; errorCode?: CoTErrorCode; toolName?: string },
  ctx: CoTErrorRenderContext,
): CoTErrorPresentation {
  const tool = friendlyTool(error.toolName ?? ctx.toolName);
  const file = ctx.fileName ? `"${ctx.fileName}"` : 'your document';
  const escalation = escalationText(ctx.failureCount, ctx);
  const quotaWarning = computeQuotaWarning(ctx);

  switch (error.errorCode) {
    case 'QUOTA_EXCEEDED': {
      if (isEnterprise(ctx)) {
        return {
          title: 'Usage limit reached',
          body: 'Your organization has reached a usage limit. Contact your admin to adjust capacity.',
          showUpgrade: false,
          retryable: false,
          cta: { label: 'Contact your admin', intent: 'admin' },
        };
      }
      return {
        title: 'Plan limit reached',
        body: isFreePlan(ctx)
          ? `You've used all your free requests this month. Upgrade to Pro for unlimited access.`
          : `You've reached a usage limit. Please try again later.`,
        showUpgrade: isFreePlan(ctx),
        retryable: false,
        cta: isFreePlan(ctx) ? { label: 'Upgrade to Pro', intent: 'upgrade' } : undefined,
        quotaWarning,
      };
    }

    case 'RATE_LIMIT':
      return {
        title: 'Too many requests',
        body: `The AI service is temporarily overloaded. Wait a moment and try again.${escalation}`,
        showUpgrade: false,
        retryable: true,
        quotaWarning,
      };

    case 'TIMEOUT':
      return {
        title: `${tool.charAt(0).toUpperCase() + tool.slice(1)} took too long`,
        body: ctx.fileName
          ? `Processing ${file} exceeded the time limit.${escalation || ' Try a smaller file or simpler request.'}`
          : `This operation took too long to complete.${escalation || ' Try again with a simpler request.'}`,
        showUpgrade: false,
        retryable: true,
        quotaWarning,
      };

    case 'TOOL_NOT_FOUND':
      return {
        title: 'Feature not available',
        body: `The ${tool} feature isn't available yet. Try a different approach.`,
        showUpgrade: false,
        retryable: false,
      };

    case 'VALIDATION_FAILED':
      return {
        title: 'Invalid input',
        body: error.message || `The input for ${tool} wasn't valid. Check your file and try again.`,
        showUpgrade: false,
        retryable: true,
      };

    case 'AUTH_REQUIRED':
      return {
        title: 'Sign-in required',
        body: 'Your session may have expired. Please sign in again to continue.',
        showUpgrade: false,
        retryable: false,
      };

    case 'NO_DOCUMENT':
    case 'NO_FILE_SELECTED':
      return {
        title: 'No document selected',
        body: 'Open or upload a document first, then try again.',
        showUpgrade: false,
        retryable: false,
      };

    case 'NO_DOCUMENT_ID':
      return {
        title: 'We couldn’t locate this document',
        body: 'We lost track of the current document for this action. Reopen the document and try again.',
        showUpgrade: false,
        retryable: false,
      };

    case 'API_ERROR':
      return {
        title: 'Service temporarily unavailable',
        body: `An external service needed for ${tool} is currently unavailable. Try again in a moment.${escalation}`,
        showUpgrade: false,
        retryable: true,
        quotaWarning,
      };

    case 'FILE_ERROR':
      return {
        title: 'File issue',
        body: ctx.fileName
          ? `There was a problem reading ${file}. Try reopening it or uploading the file again.`
          : `There was a problem with the uploaded file. Try a different file.`,
        showUpgrade: false,
        retryable: true,
      };

    case 'FILE_EMPTY':
      return {
        title: 'Document is empty',
        body: ctx.fileName
          ? `${file} doesn’t contain any readable content to process.`
          : 'This document doesn’t contain any readable content to process.',
        showUpgrade: false,
        retryable: false,
      };

    case 'FILE_TOO_SMALL':
      return {
        title: 'Document appears empty',
        body: ctx.fileName
          ? `${file} doesn’t contain enough content to process. Try uploading the full agreement.`
          : 'This file doesn’t contain enough content to process. Try uploading the full agreement.',
        showUpgrade: false,
        retryable: false,
      };

    case 'UNSUPPORTED_FORMAT':
      return {
        title: 'This file can’t be converted or signed',
        body: ctx.fileName
          ? `${file} isn’t supported for this action. Please upload a PDF, Word document, or a text file with real content.`
          : 'This document type isn’t supported for this action. Please upload a PDF, Word document, or a text file with real content.',
        showUpgrade: false,
        retryable: false,
      };

    case 'FORMAT_ERROR': {
      // Signing-specific copy
      if (error.toolName === 'esign' || error.toolName === 'sign_document') {
        return {
          title: 'Cannot sign this file type',
          body: ctx.fileName
            ? `${file} is not a supported format for signing. Upload a PDF, Word document (.docx), or text file (.txt).`
            : 'This file type is not supported for signing. Upload a PDF, Word document (.docx), or text file (.txt).',
          showUpgrade: false,
          retryable: false,
        };
      }
      // Conversion-specific copy
      if (error.toolName === 'word_to_pdf' || error.toolName === 'excel_to_pdf' || error.toolName === 'pptx_to_pdf') {
        return {
          title: 'Conversion not supported',
          body: ctx.fileName
            ? `${file} couldn't be converted to PDF. The file format may not be supported or the file may be empty.`
            : 'This file couldn\'t be converted to PDF. Try a different file format.',
          showUpgrade: false,
          retryable: false,
        };
      }
      return {
        title: 'Unsupported format',
        body: ctx.fileName
          ? `We couldn't process ${file} for ${tool}. Try uploading a PDF, Word, or text version.`
          : `This file couldn't be processed for ${tool}. Try a PDF, Word, or text version.`,
        showUpgrade: false,
        retryable: false,
      };
    }

    case 'FIELD_NOT_FOUND': {
      const signing = extractSigningContext(error.message);
      const fieldRef = signing.fieldLabel ? ` "${signing.fieldLabel}"` : '';
      const docRef = signing.documentName ? ` on "${signing.documentName}"` : ctx.fileName ? ` on ${file}` : '';
      return {
        title: 'Signing field not found',
        body: `The signature field${fieldRef} could not be located${docRef}. It may have been removed or the document was re-uploaded.`,
        showUpgrade: false,
        retryable: false,
      };
    }

    case 'SIGNATURE_FIELD_NOT_FOUND':
      return {
        title: 'Signature field not found',
        body: error.message || `We couldn't find a matching signature field on ${file}. Check the signer role or add the field manually.`,
        showUpgrade: false,
        retryable: false,
      };

    case 'FIELD_ALREADY_SIGNED': {
      const signing = extractSigningContext(error.message);
      const fieldRef = signing.fieldLabel ? ` "${signing.fieldLabel}"` : '';
      return {
        title: 'Already signed',
        body: `The field${fieldRef} has already been signed. No further action is needed.`,
        showUpgrade: false,
        retryable: false,
      };
    }

    case 'PDF_PROCESSING_ERROR':
      return {
        title: 'Document processing error',
        body: ctx.fileName
          ? `There was a problem applying the signature to ${file}. Try again or re-upload the file.`
          : 'There was a problem applying the signature to the document. Try again or re-upload the file.',
        showUpgrade: false,
        retryable: true,
      };

    case 'TOOL_FAILED':
      return {
        title: `${tool.charAt(0).toUpperCase() + tool.slice(1)} failed`,
        body: (error.message || `Something went wrong during ${tool}.`) + (escalation || ' Try again.'),
        showUpgrade: false,
        retryable: true,
        quotaWarning,
      };

    case 'UNKNOWN':
    default:
      return {
        title: 'Something went wrong',
        body: (error.message || 'An unexpected error occurred.') + escalation,
        showUpgrade: false,
        retryable: true,
        quotaWarning,
      };
  }
}
